import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class BidTagsService {

  /**
   * Decodes collection bid tags from various formats (string, object, array)
   * @param bid The bid object containing tag or tags property
   * @returns Array of decoded tag strings
   */
  decodeCollectionBidTags(bid: any): string[] {
    const payload = this.getCollectionBidTagPayload(bid);
    if (!payload) {
      return [];
    }

    if (Array.isArray(payload)) {
      return payload
        .map((entry) => this.composeTagLabel(null, entry))
        .filter((tag): tag is string => !!tag);
    }

    if (typeof payload === 'object') {
      return Object.entries(payload)
        .map(([key, value]) => this.composeTagLabel(key, value))
        .filter((tag): tag is string => !!tag);
    }

    if (typeof payload === 'string') {
      const trimmed = payload.trim();
      if (!trimmed) {
        return [];
      }

      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        const parsed = this.tryParseJson(trimmed);
        if (parsed !== null) {
          return this.decodeCollectionBidTags({ tags: parsed });
        }
      }

      // Handle single canonical cryptopunks.app tags like "Alien" or "3D_Glasses".
      if (!trimmed.includes('=') && !trimmed.includes('&') && !trimmed.includes(',')) {
        const formatted = this.formatTagSegment(trimmed);
        return formatted ? [formatted] : [];
      }

      // Handle comma-separated tags like "guy, hoodie"
      // Check if it's a simple comma-separated list (no '=' which indicates URLSearchParams format)
      if (trimmed.includes(',') && !trimmed.includes('=')) {
        return trimmed
          .split(',')
          .map((tag) => {
            const formatted = this.formatTagSegment(tag.trim());
            return formatted || null;
          })
          .filter((tag): tag is string => !!tag);
      }

      const params = new URLSearchParams(trimmed.startsWith('?') ? trimmed.slice(1) : trimmed);
      const decoded: string[] = [];
      params.forEach((value, key) => {
        const label = this.composeTagLabel(key, value);
        if (label) {
          decoded.push(label);
        }
      });
      return decoded;
    }

    return [];
  }

  /**
   * Determines if a bid is a trait bid or collection bid
   * @param bid The bid object
   * @param decodedTags Array of decoded tags
   * @returns 'trait' or 'collection'
   */
  getCollectionBidType(bid: any, decodedTags: string[]): 'trait' | 'collection' | 'custom' | 'owner' {
    // Check for owner bid - tag starts with "Owner: 0x..."
    if (decodedTags.length === 1 && decodedTags[0]?.startsWith('Owner: 0x')) {
      return 'owner';
    }
    
    // Check for custom bid from cryptopunks.app with punk_indices_count != 10000
    if (bid?.source === 'cryptopunks.app' && bid?.punk_indices_count != null) {
      const punkIndicesCount = typeof bid.punk_indices_count === 'string' 
        ? parseInt(bid.punk_indices_count, 10) 
        : bid.punk_indices_count;
      
      // If count is different from 10000, it's a custom bid
      if (!isNaN(punkIndicesCount) && punkIndicesCount > 0 && punkIndicesCount !== 10000) {
        if (decodedTags.length > 0) {
          return 'trait';
        }
        return 'custom';
      }
    }
    
    if (decodedTags.length === 1 && decodedTags[0] === 'Targeted') {
      return 'custom';
    }
    const hasIndexedTargets =
      (Array.isArray(bid?.punk_indices) && bid.punk_indices.length > 0) ||
      (Array.isArray(bid?.punkIndices) && bid.punkIndices.length > 0);

    const hasTraitMetadata =
      !!(bid?.trait || bid?.trait_type || bid?.traitType) ||
      (Array.isArray(bid?.traits) && bid.traits.length > 0);

    const tagsPayload = bid?.tags ?? bid?.tag;
    const hasRawTags = Array.isArray(tagsPayload)
      ? tagsPayload.length > 0
      : tagsPayload && typeof tagsPayload === 'object'
        ? Object.keys(tagsPayload).length > 0
        : typeof tagsPayload === 'string'
          ? tagsPayload.trim().length > 0
          : false;

    const hasTraitTargeting = decodedTags.length > 0 || hasIndexedTargets || hasTraitMetadata || hasRawTags;
    return hasTraitTargeting ? 'trait' : 'collection';
  }

  /**
   * Gets the tag payload from a bid object
   * @param bid The bid object
   * @returns The tag payload or null
   */
  private getCollectionBidTagPayload(bid: any): any {
    if (!bid) {
      return null;
    }

    const payload = bid.tags ?? bid.tag;

    if (typeof payload === 'string') {
      return payload.trim() || null;
    }

    if (Array.isArray(payload)) {
      return payload.length ? payload : null;
    }

    if (payload && typeof payload === 'object') {
      const hasEntries = Object.keys(payload).length > 0;
      return hasEntries ? payload : null;
    }

    return null;
  }

  /**
   * Composes a tag label from a key-value pair
   * @param key The tag key (optional)
   * @param value The tag value
   * @returns Formatted tag label or null
   */
  private composeTagLabel(key: string | null, value: any): string | null {
    const normalizedValue = Array.isArray(value) ? value.join(', ') : value;
    const normalizedKey = key?.toLowerCase();

    // Special handling for owner bids - preserve address format
    if (normalizedKey === 'owner' && this.isEthereumAddress(normalizedValue)) {
      return `Owner: ${String(normalizedValue).toLowerCase()}`;
    }

    const formattedValue = this.formatTagSegment(normalizedValue);
    const formattedKey = key ? this.formatTagSegment(key) : '';

    if (!formattedKey && !formattedValue) {
      return null;
    }

    if (!formattedKey) {
      return formattedValue;
    }

    if (!formattedValue) {
      return formattedKey;
    }

    return `${formattedKey}: ${formattedValue}`;
  }

  /**
   * Checks if a value is an Ethereum address
   * @param value The value to check
   * @returns true if the value is a valid Ethereum address format
   */
  private isEthereumAddress(value: any): boolean {
    if (typeof value !== 'string') {
      return false;
    }
    return /^0x[a-fA-F0-9]{40}$/i.test(value);
  }

  /**
   * Formats a tag segment (key or value) for display
   * @param value The value to format
   * @returns Formatted string
   */
  private formatTagSegment(value: string | number | boolean | null | undefined): string {
    if (value === null || value === undefined) {
      return '';
    }

    const normalized = String(value)
      .replace(/\+/g, ' ')
      .replace(/[\[\]]/g, ' ')
      .replace(/[_\-]/g, ' ')
      .replace(/^fem\s+/i, '')
      .trim();

    if (!normalized) {
      return '';
    }

    // Transform specific values: "Guy" -> "Male", "fem" -> "Female"
    const lowerNormalized = normalized.toLowerCase();
    if (lowerNormalized === 'guy') {
      return 'Male';
    }
    if (lowerNormalized === 'girl') {
      return 'Female';
    }
    if (lowerNormalized === 'fem') {
      return 'Female';
    }
    if (lowerNormalized === 'do rag') {
      return 'Do-rag';
    }
    if (lowerNormalized === '3d glasses') {
      return '3D Glasses';
    }
    if (lowerNormalized === 'vr') {
      return 'VR';
    }

    return normalized
      .split(' ')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  /**
   * Safely parses JSON string
   * @param input JSON string to parse
   * @returns Parsed object or null if parsing fails
   */
  private tryParseJson(input: string): any | null {
    try {
      return JSON.parse(input);
    } catch {
      return null;
    }
  }
}
